import type {
	ComingBucket,
	ComingStatus,
	PendingDeployment,
	Release,
	RoadmapItem,
} from './types';

/** Workflow order — the columns read left to right as work moves along. */
const COMING_BUCKETS: { status: ComingStatus; label: string }[] = [
	{ status: 'in_progress', label: 'In Progress' },
	{ status: 'up_next', label: 'Up Next' },
	{ status: 'exploring', label: 'Exploring' },
];

/** True when the item is in a deploy that has been announced but not shipped.
 *
 * A shipped item never qualifies, even if its deploy is still pending —
 * someone shipped it by hand, and it already renders under What's New.
 */
function isAwaitingDeploy(item: RoadmapItem): boolean {
	return (
		item.status !== 'shipped' &&
		item.deployment !== null &&
		item.deployment.released_on === null
	);
}

/**
 * The deploys the team has announced but not yet shipped, soonest first.
 *
 * These render as a band above the columns — "what lands this week" is the
 * most useful thing on the page, so it does not get buried in a column.
 */
export function pendingDeployments(items: RoadmapItem[]): PendingDeployment[] {
	const byName = new Map<string, PendingDeployment>();

	for (const item of items) {
		if (!isAwaitingDeploy(item)) continue;
		const deployment = item.deployment!;

		const existing = byName.get(deployment.name);
		if (existing) {
			existing.items.push(item);
		} else {
			byName.set(deployment.name, {
				name: deployment.name,
				planned_date: deployment.planned_date,
				items: [item],
			});
		}
	}

	for (const deployment of byName.values()) {
		deployment.items.sort((a, b) => a.sort_order - b.sort_order);
	}

	// Soonest first; a deploy with no date yet sorts last.
	return [...byName.values()].sort((a, b) =>
		(a.planned_date ?? '9999').localeCompare(b.planned_date ?? '9999'),
	);
}

/**
 * Splits unshipped items into the three What's Coming columns.
 *
 * Anything already in the deploy band is left out so it is not shown twice.
 * A `ready_to_deploy` item with no announced deploy folds into In Progress —
 * without that it would be published but render nowhere.
 */
export function bucketComingItems(items: RoadmapItem[]): ComingBucket[] {
	const columnItems = items.filter((item) => !isAwaitingDeploy(item));

	return COMING_BUCKETS.map(({ status, label }) => ({
		status,
		label,
		items: columnItems
			.filter(
				(item) =>
					item.status === status ||
					(status === 'in_progress' && item.status === 'ready_to_deploy'),
			)
			.sort((a, b) => a.sort_order - b.sort_order),
	}));
}

/** Groups shipped items into releases, newest release first. */
export function groupShippedByRelease(items: RoadmapItem[]): Release[] {
	const byVersion = new Map<string, Release>();

	for (const item of items) {
		if (item.status !== 'shipped') continue;

		const existing = byVersion.get(item.released_version);
		if (existing) {
			existing.items.push(item);
		} else {
			byVersion.set(item.released_version, {
				version: item.released_version,
				released_on: item.released_on,
				items: [item],
			});
		}
	}

	return [...byVersion.values()].sort((a, b) =>
		(b.released_on ?? '').localeCompare(a.released_on ?? ''),
	);
}

/**
 * Which tab a fresh open should land on.
 *
 * An explicit request always wins (the Changelog link asks for What's New).
 * Otherwise: land wherever there is something to read. Defaulting blindly to
 * What's Coming showed every user an empty box on launch day, because the
 * seed migration only creates shipped items.
 */
export function preferredTab(
	items: RoadmapItem[],
	requested?: 'coming' | 'new',
): 'coming' | 'new' {
	if (requested) return requested;

	const hasComing =
		pendingDeployments(items).length > 0 ||
		bucketComingItems(items).some((bucket) => bucket.items.length > 0);
	if (hasComing) return 'coming';

	return groupShippedByRelease(items).length > 0 ? 'new' : 'coming';
}

/**
 * Groups a release's items under area headings, in the order the areas first
 * appear — the admin's sort_order decides which area leads, not the alphabet.
 */
export function groupByArea(
	items: RoadmapItem[],
): { area: string; items: RoadmapItem[] }[] {
	const byArea = new Map<string, RoadmapItem[]>();

	for (const item of items) {
		const existing = byArea.get(item.area);
		if (existing) {
			existing.push(item);
		} else {
			byArea.set(item.area, [item]);
		}
	}

	return [...byArea.entries()].map(([area, areaItems]) => ({
		area,
		items: areaItems,
	}));
}

/**
 * The small meta line under a card: when it started, when it is promised, and
 * who is building it.
 *
 * Each part is dropped when its value is missing rather than rendered blank —
 * an empty "Promised" would read as a commitment nobody made. The date
 * formatter is injected so this stays independent of the viewer's locale.
 */
export function itemMetaParts(
	item: RoadmapItem,
	formatDate: (value: string) => string,
): string[] {
	const parts: string[] = [];

	if (item.started_date) parts.push(`Started ${formatDate(item.started_date)}`);
	if (item.promised_date) {
		parts.push(`Promised ${formatDate(item.promised_date)}`);
	}
	if (item.developer) parts.push(item.developer);

	return parts;
}

/**
 * How many items the user has not seen yet.
 *
 * The marker is the SET of ids the user has seen, not a high-water mark: the
 * Jira sync creates rows unpublished, so an item can be published (and become
 * visible) long after higher ids were already seen — a threshold would never
 * badge it. A null set means this user has never opened the window — they
 * have not "missed" anything, so they get no badge.
 */
export function countUnseen(
	items: RoadmapItem[],
	seenIds: number[] | null,
): number {
	if (seenIds === null) return 0;
	const seen = new Set(seenIds);
	return items.filter((item) => !seen.has(item.id)).length;
}

/** Every visible id — what markSeen stores as the new seen set. */
export function allIds(items: RoadmapItem[]): number[] {
	return items.map((item) => item.id);
}
